/*LIBRARY*/
import { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";

/*COMPONENT*/
import TableBody from "../mini_data_table/TableBody";
import TableHeader from "../mini_data_table/TableHeader";
import useVirtualScroll from "../mini_data_table/useVirtualScroll";
import useSearchTable from "../mini_data_table/useSearchTable";
import useSortTable from "../mini_data_table/useSortTable";

/*REDUX*/
import {
  deleteFeature,
  deleteField,
  setTabelStatus,
  set_value_layer,
  restoreFeature,
} from "../../App/actions/layerActions";
import { set_layer_id } from "../../App/actions/dataActions";
import {
  getAllFeatures,
  getAllFields,
  getLayerDetails,
} from "../../App/reduxHelper/layer";
import { set_value_properties } from "../../App/actions/propertiesActions";
import { status_action } from "../../App/actions/mapActions";

const TABLE_PARENT = ({
  layer_state,
  auth_state,
  properties_state,
  set_value_layer,
}) => {
  const [data_list, setDataList] = useState([]);
  const [fields, setFields] = useState([]);
  const [colWidths, setColWidths] = useState({});

  const fieldsRef = useRef([]);
  const scrollRef = useRef(null);

  const handleLocalDataUpdate = (rowId, fieldKey, newValue) => {
    setDataList((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          return {
            ...row,
            properties: {
              ...row.properties,
              [fieldKey]: newValue,
            },
          };
        }
        return row;
      }),
    );
  };

  const on_render = useCallback(() => {
    const layer_details = getLayerDetails(layer_state);
    const geo_layer = layer_details?.geo_layer;
    if (!geo_layer) {
      setFields([]);
      fieldsRef.current = [];
      setDataList([]);
      return;
    }
    const fields_original = getAllFields(geo_layer);
    const sortedFeatures = getAllFeatures(geo_layer);

    const filteredFields = fields_original.filter(
      (f) => !f.isStyle && f.key !== "circle_color" && !f.parent_uuid,
    );

    const newColWidths = {};
    filteredFields.forEach((f) => {
      newColWidths[f.key] = f.width ? Number(f.width) : 160;
    });

    const new_data_list = (sortedFeatures || []).map((feature, idx) => {
      const { geometry, properties = {}, ...restOfFeature } = feature;
      return {
        ...restOfFeature,
        id: idx + 1,
        key: feature?.key,
        properties: properties,
        geometry,
      };
    });

    setFields(filteredFields);
    fieldsRef.current = filteredFields;
    setDataList(new_data_list);
    setColWidths(newColWidths);
  }, [layer_state]);

  useEffect(() => {
    on_render();
  }, [on_render]);

  useEffect(() => {
    return () => {
      set_value_layer({
        key: "sorted_by",
        value: [],
      });
    };
  }, [set_value_layer]);

  const handleResizeColumn = (key, width) => {
    setColWidths((prev) => ({
      ...prev,
      [key]: width,
    }));
  };

  const activeFields = fields || [];
  const tableHeight = properties_state?.table_height || 400;
  const rowHeight = 35;
  const headerHeight = 35;
  const keyword_find = properties_state?.keyword_find || "";

  // 1. Search filter
  const filtered_data_list = useSearchTable({
    data: data_list,
    keyword: keyword_find,
  });

  // 2. Sort filter
  const { sortedData, requestSort, sortConfig } = useSortTable({
    data: filtered_data_list,
  });

  // 3. 2D Virtual Scroll
  const {
    visibleRows,
    visibleFields,
    totalWidth,
    offsetY,
    handleScroll,
    totalHeight,
  } = useVirtualScroll({
    data: sortedData || [],
    fields: activeFields,
    colWidths,
    rowHeight,
    height: tableHeight,
    overscanY: 10,
    overscanX: 3,
    containerRef: scrollRef,
  });

  const layer_details_render = getLayerDetails(layer_state);
  if (layer_details_render?.geo_layer?.is_custom_endpoint) {
    return null;
  }

  return (
    <div className="table_parent_container">
      <section className="table_parent_section">
        <div className="table_parent_inner">
          <div
            className="table_parent_scroll_container"
            ref={scrollRef}
            onScroll={handleScroll}
          >
            {/* Phantom Div (Calculates total scrollbar height) */}
            <div
              className="table_parent_phantom"
              style={{ height: totalHeight + headerHeight, width: totalWidth }}
            />

            {/* 1. STICKY HEADER */}
            <div role="table" className="table_parent_header_wrapper">
              <TableHeader
                visibleFields={visibleFields}
                totalWidth={totalWidth}
                onResize={handleResizeColumn}
                requestSort={requestSort}
                sortConfig={sortConfig}
                // needed for edit/delete functions
                fields={activeFields}
              />
            </div>

            {/* 2. VIRTUALIZED BODY */}
            <div
              role="table"
              className="table_parent_body_wrapper"
              style={{
                top: headerHeight,
                transform: `translateY(${offsetY}px) translateZ(0)`,
              }}
            >
              <TableBody
                isNested={false}
                data={visibleRows}
                fullData={sortedData || []}
                visibleFields={visibleFields}
                totalWidth={totalWidth}
                fields={activeFields} // full array needed for keyboard navigation
                onLocalDataUpdate={handleLocalDataUpdate}
                rowHeight={rowHeight}
                offsetY={offsetY}
                totalHeight={totalHeight}
                containerRef={scrollRef}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const mapStateToProps = (state) => ({
  map_state: state.map,
  layer_state: state.layer,
  auth_state: state.auth,
  properties_state: state.properties,
});

export default connect(mapStateToProps, {
  deleteFeature,
  deleteField,
  setTabelStatus,
  set_value_layer,
  restoreFeature,
  set_layer_id,
  set_value_properties,
  status_action,
})(TABLE_PARENT);
